import React, { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AnimatedTabs from '../components/ui/AnimatedTabs';
import './ServicesPage.css';

const ServicesPage = () => {
    const [activeStep, setActiveStep] = useState(0);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const tabFromUrl = queryParams.get('tab');

    useEffect(() => {
        if (tabFromUrl) {
            setTimeout(() => {
                const element = document.getElementById('tabs-section');
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 300);
        } else {
            window.scrollTo(0, 0);
        }
    }, [tabFromUrl]);

    const setupTabs = [
        {
            id: "physio",
            label: "Physiotherapy Clinic Setup",
            heading: "Physiotherapy Clinic Setup",
            content: "We design and install complete physiotherapy treatment environments for clinics and hospitals. Our setup includes treatment tables, therapy equipment placement, rehabilitation exercise zones, and optimized workflow for therapists and patients.",
            image: "https://primerehabindustries.in/Image/Home/Tab1.jpg"
        },
        {
            id: "occupational",
            label: "Occupational Therapy Setup",
            heading: "Occupational Therapy Setup",
            content: "Our occupational therapy setups are designed to support functional rehabilitation activities. These environments help patients regain independence through structured therapy programs and activity-based rehabilitation.",
            image: "https://primerehabindustries.in/Image/Home/Tab2.jpg"
        },
        {
            id: "adl",
            label: "ADL Training Environment Setup",
            heading: "ADL Training Environment Setup",
            content: "We create specialized ADL training environments where patients can practice everyday activities safely during rehabilitation. These setups simulate real-life spaces such as kitchens, bedrooms, bathrooms, and living rooms.",
            image: "https://primerehabindustries.in/Image/Home/Tab3.png"
        },
        {
            id: "pediatric",
            label:"Pediatric Therapy Setup" ,
            heading: "Pediatric Therapy Setup",
            content: "Our pediatric rehabilitation environments are designed specifically for children with developmental or physical challenges. These spaces include therapy equipment and activity zones tailored for pediatric physiotherapy and occupational therapy.",
            image: "https://primerehabindustries.in/Image/Home/Tab4.jpg"
        },
        {
            id: "complete",
            label: "Complete Rehabilitation Facility Setup",
            heading: "Complete Rehabilitation Facility Setup",
            content: "Prime Rehab Industries provides end-to-end rehabilitation facility planning and installation for hospitals, therapy centers, and rehabilitation clinics. We combine equipment supply with optimized therapy environments to create effective treatment spaces.",
            image: "https://primerehabindustries.in/Image/Home/Tab5.jpg"
        }
    ];

    const serviceCards = [
        {
            title: "Physiotherapy Clinic Setup",
            desc: "Complete physiotherapy clinic setup including treatment tables, therapy equipment placement, and rehabilitation exercise spaces designed for efficient patient care.",
            
        },
        {
            title: "Occupational Therapy Setup",
            desc: "Functional therapy environments designed to support occupational therapy treatments and help patients regain independence through rehabilitation activities.",
        },
        {
            title: "ADL Training Environment Setup",
            desc: "Complete physiotherapy clinic setup including treatment tables, therapy equipment placement, and rehabilitation exercise spaces designed for efficient patient care.",
        },
        {
            title: <>Pediatric Therapy <br />Setup</>,
            desc: "Functional therapy environments designed to support occupational therapy treatments and help patients regain independence through rehabilitation activities.",
        },
        {
            title: "Complete Rehabilitation Facility Setup",
            desc: "Complete physiotherapy clinic setup including treatment tables, therapy equipment placement, and rehabilitation exercise spaces designed for efficient patient care.",
        }
    ];

    const approachSteps = [
        {
            number: "01",
            title: "Consultation & Planning",
            desc: "We begin by understanding the requirements of your clinic or hospital. Our team evaluates the available space and identifies the best rehabilitation setup for efficient therapy delivery.",
            image: "https://primerehabindustries.in/Image/Service/Apr1.jpg"
        },
        {
            number: "02",
            title: "Customized Therapy Environment Design",
            desc: "Based on your requirements, we design specialized therapy environments including physiotherapy rooms, occupational therapy setups, and ADL training spaces.",
            image: "https://primerehabindustries.in/Image/Service/Apr2.jpg"
        },
        {
            number: "03",
            title: "Equipment Installation & Setup",
            desc: "Our team installs high-quality physiotherapy and rehabilitation equipment and ensures proper placement for effective patient treatment and therapist convenience.",
            image: "https://primerehabindustries.in/Image/Service/Apr3.jpg"
        },
        {
            number: "04",
            title: "Ongoing Support & Optimization",
            desc: "We provide support and guidance to ensure the rehabilitation facility functions efficiently and meets the evolving needs of healthcare professionals.",
            image: "https://primerehabindustries.in/Image/Service/Apr4.jpg"
        }
    ];

    const occupationalTherapy = [
        "PEG BOARD", "FINGER DEXTERITY BOARD", "CP CHAIR", "SCOOTER BOARD",
        "C – WALKER PAEDIATRIC", "ROUND SWING", "DISC SWING", "T – SWING",
        "SENSORY LEARNING PANEL", "SENSORY TYPE KIT", "BALANCE BEAM",
        "BALL POOL WITH BALL", "TRAMPOLINE", "YOGA MAT", "BOLT DEXTERITY TEST",
        "LOCK & LATCHE DEXTERITY TEST", "FLOOR COLOUR MAT"
    ];

    const exerciseTherapy = [
        "HAND EXERCISER TABLE", "ROWING MACHINE", "MULTIPLE EXERCISER CHAIR",
        "LEG & HAND EXERCISER", "CP STANDING FRAME", "WRIST ROLLER",
        "WALL LADDER", "PHYSIO GYM BALL", "WEIGHT CUFF", "FINGER GRIPPER",
        "WRIST CIRCUMDUCTOR"
    ];

    return (
        <div className="services-page">
            <Navbar />

            <main className="services-content">
                {/* HERO SECTION */}
                <section className="services-hero">
                    <div className="services-container">
                        <div className="services-hero-top">
                            <div className="services-hero-left">
                                <h1 className="services-main-title">
                                    Complete<br />
                                    Rehabilitation Setup<br />
                                    Solutions
                                </h1>
                            </div>
                            <div className="services-hero-right">
                                <p className="services-hero-desc">
                                    Prime Rehab Industries provides complete rehabilitation setup solutions for clinics, hospitals, and therapy centers. Our services include physiotherapy clinic setup, occupational therapy environments, ADL training spaces, and pediatric rehabilitation facilities designed to support effective patient recovery.
                                </p>
                                <Link to="/contact#contact-form" className="request-btn" style={{ textDecoration: 'none', display: 'inline-block' }}>
                                   Plan Your Rehab Setup
                                </Link>
                            </div>
                        </div>

                        <div className="services-hero-image">
                            <img src="https://primerehabindustries.in/Image/Service/HeroService.png" alt="Rehabilitation Setup" />
                        </div>
                    </div>
                </section>

                {/* SERVICES GRID */}
                <section className="services-grid-section">
                    <div className="services-container">
                        <h2 className="services-grid-title">Our Rehabilitation Setup Solutions</h2>
                        <div className="services-grid">
                            {serviceCards.map((card, idx) => (
                                <div className="service-card" key={idx}>
                                    <div className="service-card-header">
                                        <h3>{card.title}</h3>
                                    </div>
                                    <p>{card.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ANIMATED TABS SECTION */}
                <section className="services-tabs-section" id="tabs-section">
                    <div className="services-container">
                        <div className="tabs-content-wrapper">
                            <h2 className="tabs-section-title">Explore Specialized Setup Categories</h2>
                            <AnimatedTabs tabs={setupTabs} initialTabId={tabFromUrl} />
                        </div>
                    </div>
                </section>

                {/* SPECIALTIES SECTION */}
                <section className="specialties-section">
                    <div className="services-container">
                        <span className="specialties-subtitle">SPECIALTIES</span>
                        
                        <h2 className="specialties-title" style={{ marginTop: '0.5rem', marginBottom: '2rem' }}>OCCUPATIONAL THERAPY</h2>
                        <div className="specialties-grid">
                            {occupationalTherapy.map((item, idx) => (
                                <div className="specialty-card" key={idx}>
                                    <span className="specialty-text">{item}</span>
                                </div>
                            ))}
                        </div>

                        <h2 className="specialties-title" style={{ marginTop: '4rem', marginBottom: '2rem' }}>EXERCISE THERAPY</h2>
                        <div className="specialties-grid">
                            {exerciseTherapy.map((item, idx) => (
                                <div className="specialty-card" key={idx}>
                                    <span className="specialty-text">{item}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* OUR APPROACH SECTION */}
                <section className="approach-section">
                    <div className="services-container">
                        <div className="approach-flex">
                            <div className="approach-left">
                                <h2 className="approach-title">
                                    Our Approach to<br />
                                    Rehabilitation Facility<br />
                                    Setup
                                </h2>
                                <div className="approach-main-img">
                                    <img src="https://primerehabindustries.in/Image/Home/BgHero1.png" alt="Laboratory" />
                                </div>
                            </div>
                            
                            <div className="approach-right">
                                <div className="approach-accordion">
                                    {approachSteps.map((step, index) => (
                                        <div 
                                            key={index} 
                                            className={`approach-item ${activeStep === index ? 'active' : ''}`}
                                            onClick={() => setActiveStep(index)}
                                        >
                                            <div className="approach-item-header">
                                                <span className="step-num">{step.number}</span>
                                                <h3 className="step-title">{step.title}</h3>
                                                <span className="toggle-icon">{activeStep === index ? '−' : '+'}</span>
                                            </div>
                                            
                                            <div className="approach-item-content">
                                                <div className="content-inner">
                                                    <p>{step.desc}</p>
                                                    <div className="step-img">
                                                        <img src={step.image} alt={step.title} />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* IMAGE CAROUSEL SECTION */}
                <section className="service-image-carousel">
                    <div className="services-container">
                        <h2 className="carousel-section-title">Completed Rehabilitation Facility Setups</h2>
                    </div>
                    <div className="carousel-track-viewport">
                        <motion.div 
                            className="carousel-track"
                            animate={{
                                x: [0, -2410],
                            }}
                            transition={{
                                x: {
                                    repeat: Infinity,
                                    repeatType: "loop",
                                    duration: 30,
                                    ease: "linear",
                                },
                            }}
                        >
                            {[...Array(2)].map((_, i) => (
                                <React.Fragment key={i}>
                                    <div className="carousel-item"><img src="https://primerehabindustries.in/Image/Service/caro1.jpeg" alt="Setup 1" /></div>
                                    <div className="carousel-item"><img src="https://primerehabindustries.in/Image/Service/caro2.jpeg" alt="Setup 2" /></div>
                                    <div className="carousel-item"><img src="https://primerehabindustries.in/Image/Service/caro3.jpeg" alt="Setup 3" /></div>
                                    <div className="carousel-item"><img src="https://primerehabindustries.in/Image/Service/caro4.jpeg" alt="Setup 4" /></div>
                                    <div className="carousel-item"><img src="https://primerehabindustries.in/Image/Service/caro5.jpeg" alt="Setup 5" /></div>
                                </React.Fragment>
                            ))}
                        </motion.div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default ServicesPage;
